/**
 * Warning Context
 *
 * Provides global warning state management for the application.
 * Warnings are displayed in a modal and can be dismissed by the user.
 */

import { createContext, useState, useCallback, type ReactNode } from 'react';
import type { Warning, WarningContextValue } from './types';

export const WarningContext = createContext<WarningContextValue | undefined>(undefined);

// Generate unique ID with fallback for older browsers
const generateId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

// Dedupe window in milliseconds - ignore duplicate messages within this time
const DEDUPE_WINDOW_MS = 2000;

// Maximum number of warnings to keep in queue
const MAX_WARNINGS = 5;

interface WarningProviderProps {
  children: ReactNode;
}

export function WarningProvider({ children }: WarningProviderProps) {
  const [warnings, setWarnings] = useState<Warning[]>([]);

  const showWarning = useCallback((warning: Omit<Warning, 'id' | 'timestamp'>) => {
    const id = generateId();
    const timestamp = Date.now();

    setWarnings(prev => {
      // Dedupe: skip if same message within dedupe window
      const isDupe = prev.some(w =>
        w.message === warning.message &&
        timestamp - w.timestamp < DEDUPE_WINDOW_MS
      );
      if (isDupe) {
        return prev;
      }

      // Limit queue size - keep most recent warnings
      const limited = prev.slice(-(MAX_WARNINGS - 1));
      return [...limited, { ...warning, id, timestamp }];
    });
  }, []);

  const dismissWarning = useCallback((id: string) => {
    setWarnings(prev => prev.filter(w => w.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    setWarnings([]);
  }, []);

  return (
    <WarningContext.Provider value={{ warnings, showWarning, dismissWarning, dismissAll }}>
      {children}
    </WarningContext.Provider>
  );
}
